/**
 * DeviceHome - Main page for device.hayatfamily.app
 * 
 * CRITICAL FIX (Dec 2024):
 * The "TypeError: A is not a function" crash was caused by attempting to call
 * non-existent functions (connectWS, disconnectWS) from useDeviceCallWebSocket.
 * 
 * The hook was incorrectly destructured - it does NOT export connect/disconnect.
 * Instead, it auto-connects internally when deviceId changes.
 * 
 * Fixed by:
 * 1. Correctly destructuring the hook's actual return values
 * 2. Removing manual WebSocket connection logic (now handled by the hook)
 * 3. Ensuring all useEffect cleanups return functions or undefined
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Bell, Phone, PhoneOff, Moon, Clock, Home as HomeIcon, Book, Heart, Music, Video, AlertCircle, Settings, Users, BookOpen, Headphones, Cloud, Newspaper, Mic } from 'lucide-react';
import { useWebRTC } from '../webrtc/useWebRTC.ts';
import { useDeviceCallWebSocket } from '../calls/useDeviceCallWebSocket.ts';
import { useDeviceSettings } from '../contexts/DeviceSettingsContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import VideoDisplay from '../components/calls/VideoDisplay';
import IncomingCallModal from '../components/device/IncomingCallModal';
import BottomNav from '../components/device/BottomNav';

// New Views
import WeatherView from '../components/device/WeatherView';
import NewsView from '../components/device/NewsView';
import StoriesView from '../components/device/StoriesView';
import MusicView from '../components/device/MusicView';
import ContactsView from '../components/device/ContactsView';
import RemindersView from '../components/device/RemindersView';
import ReminderList from '../components/device/ReminderList.tsx';
import ReminderForm from '../components/device/ReminderForm.tsx';
import EmergencyView from '../components/device/EmergencyView';
import SettingsView from '../components/device/SettingsView';
import PrayerTimesView from '../components/PrayerTimesView';
import MicButton from '../components/device/MicButton';
import VoiceOverlay from '../components/device/VoiceOverlay';
import GlobalAudioPlayer from '../components/device/NowPlayingWidget';
import MiniPlayer from '../components/device/MiniPlayer';
import { useVoiceControl } from '../hooks/useVoiceControl';
import { useLiveClock } from '../hooks/useLiveClock';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import ScreensaverView from '../components/device/ScreensaverView';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeviceHome() {
  // URL params
  const { deviceId: urlDeviceId } = useParams();
  const navigate = useNavigate();

  // Settings context
  const { settings } = useDeviceSettings();

  // Core state
  const [deviceId, setDeviceId] = useState(null);
  const [device, setDevice] = useState(null);
  const [senior, setSenior] = useState(null);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isSetupMode, setIsSetupMode] = useState(false);
  
  // Skills state (P1: Skills-based feature toggling)
  const [householdSkills, setHouseholdSkills] = useState([]);
  
  // View state
  const [currentView, setCurrentView] = useState('home');
  const [reminders, setReminders] = useState([]);
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [emergencies, setEmergencies] = useState([]);
  
  // Reminder sub-navigation state
  const [reminderSubView, setReminderSubView] = useState('list'); // 'list' | 'new' | 'edit'
  const [editingReminderId, setEditingReminderId] = useState(null);
  
  // P2: Connection status tracking for toast
  const [previousConnectionState, setPreviousConnectionState] = useState(null);
  const [screensaverPhotos, setScreensaverPhotos] = useState([]);
  
  // ===================================================================
  // CUSTOM HOOKS - ALL AT TOP LEVEL
  // ===================================================================
  
  // Global Audio Player Hook
  const audioPlayer = useAudioPlayer();
  
  // WebRTC Hook
  const webrtc = useWebRTC();
  
  // WebSocket Hook for Call Management
  // Note: This hook auto-connects internally when deviceId changes
  const {
    currentCall,
    acceptIncomingCall,
    rejectIncomingCall,
    sendSignal: sendWebRTCSignal
  } = useDeviceCallWebSocket(deviceId);
  
  // P2: Live Clock Hook
  const liveClock = useLiveClock({ 
    withSeconds: settings.clock?.showSeconds ?? false,
    locale: settings.language === 'tr' ? 'tr-TR' : 'de-DE'
  });
  
  // P2: Screensaver/Inactivity Hook
  const screensaver = useInactivityTimer({ 
    timeoutMinutes: settings.screensaver?.startAfterMinutes ?? 5
  });
  
  // Voice Control Hook
  const handleVoiceCommand = (command) => {
    console.log('[DeviceHome] Voice command received:', command);
    
    switch (command) {
      case 'weather':
        setCurrentView('weather');
        toast.success('Wetter wird geöffnet');
        break;
      case 'news':
        setCurrentView('news');
        toast.success('Nachrichten werden geöffnet');
        break;
      case 'music':
        setCurrentView('music');
        toast.success('Musik wird geöffnet');
        break;
      case 'stories':
        setCurrentView('stories');
        toast.success('Geschichten werden geöffnet');
        break;
      case 'prayer_times':
        setCurrentView('prayer_times');
        toast.success('Gebetszeiten werden geöffnet');
        break;
      case 'emergency':
        setCurrentView('emergency');
        toast.success('Notfall wird geöffnet');
        break;
      case 'call':
        setCurrentView('call');
        toast.success('Familie wird angerufen');
        break;
      case 'stop':
        // Stop any playing audio (Radio via global audio player)
        if (audioPlayer.isPlaying) {
          audioPlayer.stop();
          toast.success('Radio gestoppt');
        } else {
          toast('Keine Wiedergabe aktiv');
        }
        break;
      case 'home':
        setCurrentView('home');
        toast.success('Zurück zur Startseite');
        break;
      default:
        console.warn('[DeviceHome] Unknown voice command:', command);
    }
  };
  
  const voiceControl = useVoiceControl({
    onCommand: handleVoiceCommand
  });
  
  // ===================================================================
  // EFFECT: Initialize device_id (from URL or localStorage)
  // ===================================================================
  
  useEffect(() => {
    if (urlDeviceId) {
      console.log(`[Device] device_id from URL: ${urlDeviceId}`);
      setDeviceId(urlDeviceId);
      localStorage.setItem('hayat_device_id', urlDeviceId);
    } else {
      const storedId = localStorage.getItem('hayat_device_id');
      if (storedId) {
        console.log(`[Device] device_id from localStorage: ${storedId}`);
        setDeviceId(storedId);
      } else {
        console.log('[Device] No device_id found, need setup');
        setIsSetupMode(true);
        setLoading(false);
      }
    }
  }, [urlDeviceId]);

  // ===================================================================
  // EFFECT: Connect WebRTC signaling to WebSocket
  // ===================================================================
  
  useEffect(() => {
    if (!webrtc || !sendWebRTCSignal) return undefined;

    // Forward WebRTC signals to WebSocket
    webrtc.onSignal = (signal) => {
      console.log('[DeviceHome] Sending WebRTC signal via WS:', signal.type);
      sendWebRTCSignal(signal);
    };

    return () => {
      if (webrtc) {
        webrtc.onSignal = null;
      }
    };
  }, [webrtc, sendWebRTCSignal]);

  // ===================================================================
  // EFFECT: Connect WebSocket when deviceId is available
  // ===================================================================
  // NOTE: WebSocket connection is now handled automatically by useDeviceCallWebSocket.
  // The hook auto-connects when deviceId changes and auto-disconnects on unmount.
  // No manual connect/disconnect needed here.

  // ===================================================================
  // EFFECT: Handle incoming WebRTC signals from WebSocket
  // ===================================================================
  
  useEffect(() => {
    if (!currentCall || !webrtc) return undefined;

    // If we receive a WebRTC signal from the other peer via WebSocket
    if (currentCall.webrtcSignal && currentCall.status === 'in_call') {
      console.log('[DeviceHome] Received WebRTC signal from WS:', currentCall.webrtcSignal.type);
      if (typeof webrtc.handleRemoteSignal === 'function') {
        webrtc.handleRemoteSignal(currentCall.webrtcSignal);
      }
    }
  }, [currentCall, webrtc]);

  // ===================================================================
  // EFFECT: Fetch device data on mount
  // ===================================================================
  
  useEffect(() => {
    if (!deviceId || isSetupMode) return undefined;
    
    fetchDeviceData();
  }, [deviceId, isSetupMode]);

  // ===================================================================
  // EFFECT: Send heartbeat every 30 seconds
  // ===================================================================
  
  useEffect(() => {
    if (!deviceId || isSetupMode || !device) return undefined;
    
    const interval = setInterval(sendHeartbeat, 30000);
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [deviceId, device, isSetupMode]);

  // ===================================================================
  // EFFECT: P2 - Connection Status Toast (show only on transition)
  // ===================================================================
  
  useEffect(() => {
    if (!device || !household) return;
    
    const currentState = device.connection_state;
    
    // Only show toast when transitioning from offline to online
    if (previousConnectionState === 'offline' && currentState === 'online') {
      toast.success(`Verbunden mit ${household.name}`, {
        duration: 4000,
        position: 'top-center'
      });
    }
    
    setPreviousConnectionState(currentState);
  }, [device?.connection_state, household?.name]);

  // ===================================================================
  // EFFECT: P2 - Close screensaver on emergency/call/reminder
  // ===================================================================
  
  useEffect(() => {
    if (currentCall || currentView === 'emergency' || reminders.length > 0) {
      if (screensaver.isScreensaverActive) {
        console.log('[DeviceHome] Emergency/Call/Reminder detected, closing screensaver');
        screensaver.resetTimer();
      }
    }
  }, [currentCall, currentView, reminders.length, screensaver]);

  // ===================================================================
  // DATA FETCHING FUNCTIONS
  // ===================================================================
  
  // Load backend settings and hydrate context
  const loadBackendSettings = async (seniorId) => {
    try {
      console.log('[DeviceHome] Loading backend settings for senior:', seniorId);
      const response = await axios.get(`${API}/api/seniors/${seniorId}/settings`);
      const backendSettings = response.data;
      
      console.log('[DeviceHome] Backend settings loaded:', backendSettings);
      
      // Store in localStorage for offline access
      localStorage.setItem('hayat_backend_settings', JSON.stringify(backendSettings));
      
      return backendSettings;
    } catch (error) {
      console.error('[DeviceHome] Error loading backend settings:', error);
      
      // Try to load from localStorage cache
      const cached = localStorage.getItem('hayat_backend_settings');
      if (cached) {
        console.log('[DeviceHome] Using cached backend settings');
        return JSON.parse(cached);
      }
      
      return null;
    }
  };

  // P1: Helper function to check if a skill is enabled
  const isSkillEnabled = (skillKey) => {
    // If no skills loaded yet, default to enabled (graceful degradation)
    if (!householdSkills || householdSkills.length === 0) {
      return true;
    }
    
    // Find skill by key
    const skillEntry = householdSkills.find(s => s.skill?.key === skillKey);
    
    // If skill not found, default to enabled
    if (!skillEntry) {
      return true;
    }
    
    // Return enabled status
    return skillEntry.enabled === true;
  };

  const fetchDeviceData = async () => {
    if (!deviceId) {
      console.error('[Device] ❌ Cannot fetch device data: No device_id');
      setLoading(false);
      return;
    }
    
    console.log(`[Device] Fetching device data for device_id: ${deviceId}`);
    setLoading(true);
    setLoadingError(null);
    
    try {
      // Try to use /dashboard endpoint (single source of truth)
      console.log(`[Device] 🔍 GET /api/devices/${deviceId}/dashboard`);
      
      const deviceRes = await axios.get(`${API}/api/devices/${deviceId}/dashboard`, { 
        timeout: 10000 
      });
      
      console.log(`[Device] ✅ Dashboard loaded successfully`);
      
      // Dashboard endpoint returns { device, senior, household }
      const { device: deviceData, senior: seniorData, household: householdData } = deviceRes.data;
      
      setDevice(deviceData);
      setSenior(seniorData || null);
      setHousehold(householdData || null);
      
      console.log(`[Device] ✅ Device: ${deviceData?.device_name}`);
      console.log(`[Device] ✅ Senior: ${seniorData?.name || 'None'}`);
      console.log(`[Device] ✅ Household: ${householdData?.name || 'None'}`);
      
      // P1: Load SENIOR skills (for per-senior feature toggling)
      if (seniorData?.id) {
        try {
          const skillsRes = await axios.get(`${API}/api/seniors/${seniorData.id}/skills`, {
            timeout: 5000
          });
          setHouseholdSkills(skillsRes.data.skills || []);
          console.log(`[Device] ✅ Senior skills loaded: ${skillsRes.data.skills?.length || 0} skills`);
        } catch (skillsError) {
          console.warn('[Device] ⚠️ Senior skills fetch failed (non-critical):', skillsError);
          // Non-critical: Default to all skills enabled (graceful degradation)
          setHouseholdSkills([]);
        }
      }
      
    } catch (error) {
      console.error('[Device] ❌ Dashboard fetch failed:', error);
      
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      // Handle specific error cases with proper cleanup and user guidance
      if (status === 404) {
        console.error('[Device] ❌ Device not found (404)');
        setLoadingError({
          type: '404',
          title: 'Gerät nicht gefunden',
          message: 'Dieses Gerät wurde nicht gefunden. Bitte koppeln Sie das Gerät erneut mit Ihrer Familie.',
          action: 'setup'
        });
        
        // Clear invalid device_id
        localStorage.removeItem('hayat_device_id');
        
        // Redirect after delay
        setTimeout(() => {
          window.location.href = '/device/setup';
        }, 3000);
        
      } else if (status === 409) {
        console.warn('[Device] ⚠️ Device not paired (409)');
        console.warn('[Device] Detail:', detail);
        
        setLoadingError({
          type: '409',
          title: 'Gerät nicht gekoppelt',
          message: 'Dieses Gerät ist noch nicht mit einem Senior gekoppelt. Bitte verwenden Sie die Family-App, um das Gerät zu koppeln.',
          action: 'setup'
        });
        
        // Clear invalid pairing
        localStorage.removeItem('hayat_device_id');
        
        // Redirect after delay
        setTimeout(() => {
          window.location.href = '/device/setup';
        }, 3000);
        
      } else if (status === 401 || status === 403) {
        console.warn('[Device] ⚠️ Authentication/Authorization error');
        
        setLoadingError({
          type: '401',
          title: 'Verbindung abgelaufen',
          message: 'Die Verbindung zu Ihrer Familie ist abgelaufen. Bitte koppeln Sie das Gerät erneut.',
          action: 'setup'
        });
        
      } else if (error.message === 'Network Error' || !error.response) {
        console.error('[Device] ❌ Network error');
        
        setLoadingError({
          type: 'network',
          title: 'Keine Verbindung',
          message: 'Es konnte keine Verbindung zum Server hergestellt werden. Bitte überprüfen Sie Ihre Internetverbindung.',
          action: 'retry'
        });
        
      } else {
        console.error('[Device] ❌ Unknown error:', status);
        
        setLoadingError({
          type: 'unknown',
          title: 'Fehler beim Laden',
          message: detail || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
          action: 'retry'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const sendHeartbeat = async () => {
    if (!deviceId || !device) {
      console.warn('[Device] Heartbeat skipped: deviceId or device not available');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/devices/${deviceId}/heartbeat`, {
        metrics: {
          timestamp: new Date().toISOString(),
          online: true
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[Device] ✓ Heartbeat OK', {
        deviceId,
        connectionState: response.data?.connection_state,
        status: response.data?.status
      });
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      console.error('[Device] Heartbeat failed:', {
        deviceId,
        status,
        detail,
        deviceStatus: device?.status,
        devicePaired: device?.status === 'paired'
      });
      
      // Don't spam UI with toast errors for heartbeat failures
      // Server connection issues are already shown in status bar
    }
  };

  // ===================================================================
  // CALL HANDLERS
  // ===================================================================
  
  const handleAcceptCall = async () => {
    console.log('[DeviceHome] Accepting call...');
    
    if (!currentCall || !currentCall.callId) {
      console.error('[DeviceHome] No active call to accept');
      return;
    }
    
    try {
      // Pause audio player during call
      if (audioPlayer.isPlaying) {
        console.log('[DeviceHome] Pausing audio for incoming call');
        audioPlayer.pause();
      }
      
      // 1. Accept via WebSocket
      if (typeof acceptIncomingCall === 'function') {
        await acceptIncomingCall(currentCall.callId);
      }
      
      // 2. Start WebRTC as answerer
      console.log('[DeviceHome] Starting WebRTC as answerer...');
      if (webrtc && typeof webrtc.startCall === 'function') {
        await webrtc.startCall(false); // false = answerer
      }
      
    } catch (error) {
      console.error('[DeviceHome] Error accepting call:', error);
      toast.error('Fehler beim Annehmen des Anrufs');
    }
  };

  const handleRejectCall = async () => {
    console.log('[DeviceHome] Rejecting/ending call...');
    
    if (!currentCall || !currentCall.callId) {
      console.error('[DeviceHome] No active call to reject');
      return;
    }
    
    try {
      // 1. Reject via WebSocket
      if (typeof rejectIncomingCall === 'function') {
        await rejectIncomingCall(currentCall.callId);
      }
      
      // 2. Stop WebRTC
      if (webrtc && typeof webrtc.endCall === 'function') {
        webrtc.endCall();
      }
      
    } catch (error) {
      console.error('[DeviceHome] Error rejecting call:', error);
    }
  };

  const handleCallFamily = () => {
    console.log('[DeviceHome] Call Family requested');
    setCurrentView('call');
  };

  const handleCallContact = (contact) => {
    console.log('[DeviceHome][Contacts] Call requested for contact:', contact.name);
    toast.info(`Anruf an ${contact.name} wird vorbereitet...`);
    // TODO: Implement contact call via WebSocket when backend supports it
    // For now, just show the call view
    setCurrentView('call');
  };

  // ===================================================================
  // UTILITY FUNCTIONS
  // ===================================================================
  
  const getTranslation = (key) => {
    const translations = {
      welcome: 'Willkommen',
      reminders: 'Erinnerungen',
      callFamily: 'Familie anrufen',
      prayerTimes: 'Gebetszeiten',
      quran: 'Heilige Schrift',
      stories: 'Geschichten',
      music: 'Musik',
      emergency: 'Notfall',
      settings: 'Einstellungen'
    };
    return translations[key] || key;
  };

  // ===================================================================
  // RENDER: LOADING STATE
  // ===================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-2xl font-semibold text-gray-800">Lädt...</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // RENDER: ERROR STATE
  // ===================================================================

  if (loadingError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            {loadingError.type === 'network' ? (
              <AlertCircle className="w-24 h-24 mx-auto text-orange-500" />
            ) : (
              <AlertCircle className="w-24 h-24 mx-auto text-red-500" />
            )}
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {loadingError.title}
          </h2>
          
          <p className="text-xl text-gray-700 mb-8">
            {loadingError.message}
          </p>
          
          <div className="space-y-4">
            {loadingError.action === 'setup' && (
              <button
                onClick={() => window.location.href = '/device/setup'}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xl font-bold rounded-xl hover:shadow-lg transition-all"
              >
                Zum Geräte-Setup
              </button>
            )}
            
            {loadingError.action === 'retry' && (
              <button
                onClick={() => {
                  setLoadingError(null);
                  fetchDeviceData();
                }}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xl font-bold rounded-xl hover:shadow-lg transition-all"
              >
                Erneut versuchen
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===================================================================
  // RENDER: SETUP MODE
  // ===================================================================

  if (isSetupMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Gerät einrichten
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Bitte verbinden Sie dieses Gerät mit Ihrer Familie über die Family-App.
          </p>
          <button
            onClick={() => navigate('/device/setup')}
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xl font-bold rounded-xl hover:shadow-lg transition-all"
          >
            Gerät einrichten
          </button>
        </div>
      </div>
    );
  }

  // ===================================================================
  // RENDER: WAITING FOR DATA
  // ===================================================================

  if (!device || !senior) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Gerät wird geladen...
          </h2>
          <p className="text-xl text-gray-700">
            Bitte warten Sie einen Moment.
          </p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // RENDER: MAIN DEVICE HOME SCREEN
  // ===================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 md:p-6">
      {/* Incoming Call Modal */}
      {currentCall && currentCall.status === 'incoming' && (
        <IncomingCallModal
          callId={currentCall.callId}
          fromUserName={currentCall.fromUserName || 'Familienmitglied'}
          seniorName={currentCall.seniorName}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}
      
      {/* Active Video Call */}
      {currentCall && currentCall.status === 'in_call' && webrtc && (webrtc.localStream || webrtc.remoteStream) && (
        <div className="fixed inset-0 z-50 bg-black">
          <VideoDisplay
            localStream={webrtc.localStream}
            remoteStream={webrtc.remoteStream}
            isAudioEnabled={webrtc.isAudioEnabled}
            isVideoEnabled={webrtc.isVideoEnabled}
            onToggleAudio={webrtc.toggleAudio}
            onToggleVideo={webrtc.toggleVideo}
            layout="pip"
          />
          
          {/* End Call Button */}
          <button
            onClick={handleRejectCall}
            className="absolute top-6 right-6 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg flex items-center gap-2"
          >
            <PhoneOff className="w-6 h-6" />
            Anruf beenden
          </button>
        </div>
      )}

      {/* P2: Offline Status Bar (only show when offline) */}
      {device && device.connection_state !== 'online' && (
        <div className="fixed bottom-20 left-0 right-0 z-40 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-500 border-2 border-red-600 rounded-xl px-6 py-3 shadow-2xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl animate-pulse">🔴</span>
                <div>
                  <p className="text-lg font-bold text-white">
                    Offline – versuche Verbindung wiederherzustellen...
                  </p>
                  {device.last_active && (
                    <p className="text-sm text-white/80">
                      Letzte Aktivität: {new Date(device.last_active).toLocaleTimeString('de-DE', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Status Badge (top-right when in call) */}
      {currentCall && (currentCall.status === 'ringing' || currentCall.status === 'in_call') && (
        <div className="fixed top-4 right-4 z-40">
          <div className="flex items-center gap-2 bg-cyan-500 px-5 py-3 rounded-full shadow-xl">
            <Phone className="w-5 h-5 text-white animate-pulse" />
            <span className="text-base font-bold text-white">
              {currentCall.status === 'ringing' ? 'Anruf läuft...' : 'Im Videoanruf'}
            </span>
          </div>
        </div>
      )}

      {/* Mini Player - Compact header player (only when audio is active) */}
      {currentView === 'home' && (
        <div className="fixed top-4 left-4 right-4 z-30">
          <MiniPlayer />
        </div>
      )}

      {/* HOME VIEW */}
      {currentView === 'home' && (
        <>
          {/* Header with Senior Name and Clock */}
          <div className="text-center mb-6">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
              {getTranslation('welcome')}, {senior.name}!
            </h1>
            
            {/* P2: Live Clock - Dynamic Size */}
            {settings.clock?.size === 'small' && (
              <div className="text-2xl md:text-3xl text-gray-700 font-semibold">
                <Clock className="w-8 h-8 inline mr-2" />
                {liveClock.timeString}
                {settings.clock?.showDate && (
                  <span className="block text-xl text-gray-600 mt-1">{liveClock.dateString}</span>
                )}
              </div>
            )}
            
            {settings.clock?.size === 'medium' && (
              <div className="my-8">
                <div className="text-5xl md:text-6xl font-bold text-blue-700 mb-2">
                  {liveClock.timeString}
                </div>
                {settings.clock?.showDate && (
                  <div className="text-2xl text-gray-600">{liveClock.dateString}</div>
                )}
              </div>
            )}
            
            {settings.clock?.size === 'large' && (
              <div className="my-12">
                <div className="text-7xl md:text-8xl lg:text-9xl font-bold text-blue-800 tracking-wider mb-4">
                  {liveClock.timeString}
                </div>
                {settings.clock?.showDate && (
                  <div className="text-3xl md:text-4xl text-gray-700 font-medium">{liveClock.dateString}</div>
                )}
              </div>
            )}
          </div>

          {/* Tiles Grid - Dynamic padding for audio player */}
          <div 
            className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            style={{ paddingBottom: audioPlayer.currentStation ? '140px' : '20px' }}
          >
        {/* Weather Tile (P1: Skills-based) */}
        {isSkillEnabled('weather') && (
          <button
            onClick={() => setCurrentView('weather')}
            className="aspect-square bg-white border-3 border-blue-400 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <Cloud className="w-16 h-16 mb-3 text-blue-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">Wetter</span>
          </button>
        )}

        {/* News Tile (P1: Skills-based) */}
        {isSkillEnabled('news') && (
          <button
            onClick={() => setCurrentView('news')}
            className="aspect-square bg-white border-3 border-orange-400 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <Newspaper className="w-16 h-16 mb-3 text-orange-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">Nachrichten</span>
          </button>
        )}

        {/* Reminders Tile (P1: CORE - always enabled) */}
        {isSkillEnabled('reminders') && (
          <button
            onClick={() => {
              setCurrentView('reminders');
              setReminderSubView('list'); // Reset to list view
            }}
            className="aspect-square bg-white border-3 border-green-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <Bell className="w-16 h-16 mb-3 text-green-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{getTranslation('reminders')}</span>
          </button>
        )}

        {/* Call Family Tile (P1: CORE - always enabled) */}
        {isSkillEnabled('video_calls') && (
          <button
            onClick={() => setCurrentView('call')}
            className="aspect-square bg-white border-3 border-blue-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <Phone className="w-16 h-16 mb-3 text-blue-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{getTranslation('callFamily')}</span>
          </button>
        )}

        {/* Prayer Times Tile (P1: Skills-based + religion check) */}
        {isSkillEnabled('prayer_times') && (senior?.religion === 'islam' || senior?.religion === 'christian') && (
          <button
            onClick={() => setCurrentView('prayer_times')}
            className="aspect-square bg-white border-3 border-purple-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <div className="relative mb-2">
              <Moon className="w-16 h-16 text-purple-600" strokeWidth={2.5} />
              <div className="absolute -top-1 -right-1 text-2xl">
                {senior?.religion === 'islam' && '☪️'}
                {senior?.religion === 'christian' && '✝️'}
              </div>
            </div>
            <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">{getTranslation('prayerTimes')}</span>
          </button>
        )}

        {/* Emergency Tile (P1: CORE - always enabled) */}
        {isSkillEnabled('emergency') && (
          <button
            onClick={() => setCurrentView('emergency')}
            className="aspect-square bg-white border-3 border-red-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <AlertCircle className="w-16 h-16 mb-3 text-red-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{getTranslation('emergency')}</span>
          </button>
        )}

        {/* Stories Tile (P1: Skills-based) */}
        {isSkillEnabled('stories') && (
          <button
            onClick={() => setCurrentView('stories')}
            className="aspect-square bg-white border-3 border-amber-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <BookOpen className="w-16 h-16 mb-3 text-amber-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">Geschichten</span>
          </button>
        )}

        {/* Music Tile (P1: Skills-based) */}
        {isSkillEnabled('music_radio') && (
          <button
            onClick={() => setCurrentView('music')}
            className="aspect-square bg-white border-3 border-pink-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
          >
            <Headphones className="w-16 h-16 mb-3 text-pink-600" strokeWidth={2.5} />
            <span className="text-2xl md:text-3xl font-bold text-gray-900">Musik</span>
          </button>
        )}

        {/* Contacts Tile */}
        <button
          onClick={() => setCurrentView('contacts')}
          className="aspect-square bg-white border-3 border-teal-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
        >
          <Users className="w-16 h-16 mb-3 text-teal-600" strokeWidth={2.5} />
          <span className="text-2xl md:text-3xl font-bold text-gray-900">Kontakte</span>
        </button>

        {/* Settings Tile (P1: ALWAYS visible - no skill check) */}
        <button
          onClick={() => setCurrentView('settings')}
          className="aspect-square bg-white border-3 border-gray-500 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex flex-col items-center justify-center p-4 text-center"
        >
          <Settings className="w-16 h-16 mb-3 text-gray-600" strokeWidth={2.5} />
          <span className="text-2xl md:text-3xl font-bold text-gray-900">Einstellungen</span>
        </button>
          </div>
        </>
      )}

      {/* REMINDERS VIEW with Sub-Navigation */}
      {currentView === 'reminders' && senior && (
        <>
          {reminderSubView === 'list' && (
            <ReminderList 
              senior={senior}
              onNavigateNew={() => setReminderSubView('new')}
              onNavigateEdit={(id) => {
                setEditingReminderId(id);
                setReminderSubView('edit');
              }}
            />
          )}
          {reminderSubView === 'new' && (
            <ReminderForm 
              mode="create"
              senior={senior}
              onBack={() => setReminderSubView('list')}
              onSuccess={() => setReminderSubView('list')}
            />
          )}
          {reminderSubView === 'edit' && editingReminderId && (
            <ReminderForm 
              mode="edit"
              reminderId={editingReminderId}
              senior={senior}
              onBack={() => setReminderSubView('list')}
              onSuccess={() => setReminderSubView('list')}
            />
          )}
        </>
      )}

      {/* CALL VIEW */}
      {currentView === 'call' && (
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              <Phone className="w-16 h-16 inline mr-4 text-blue-600" />
              Familie anrufen
            </h1>
          </div>

          <div className="space-y-6">
            {/* Call Status */}
            {currentCall && currentCall.status === 'ringing' && (
              <div className="bg-blue-100 border-2 border-blue-500 rounded-2xl p-6 text-center">
                <Phone className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-pulse" />
                <p className="text-2xl font-bold text-blue-900">
                  Rufe Familie an...
                </p>
              </div>
            )}

            {/* Call Button */}
            {!currentCall && (
              <div className="text-center">
                <button
                  onClick={async () => {
                    try {
                      console.log('[DeviceHome] Starting call to family...');
                      toast.success('Anruf wird gestartet...');
                      // Note: Actual call logic requires family member selection
                      // This is a placeholder - real implementation needs device/senior selection
                    } catch (error) {
                      console.error('[DeviceHome] Failed to start call:', error);
                      toast.error('Anruf konnte nicht gestartet werden');
                    }
                  }}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-12 py-8 rounded-2xl shadow-2xl hover:shadow-3xl transition-all text-3xl font-bold flex items-center gap-4 mx-auto"
                >
                  <Phone className="w-12 h-12" />
                  Familie kontaktieren
                </button>
                
                <p className="text-xl text-gray-600 mt-6">
                  Ihre Familie wird benachrichtigt
                </p>
              </div>
            )}

            {/* Information Box */}
            <div className="bg-white border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                ℹ️ Videoanruf-Funktion
              </h3>
              <ul className="space-y-3 text-xl text-gray-700">
                <li>✓ Ihre Familie kann Sie jederzeit anrufen</li>
                <li>✓ Sie werden durch einen Ton benachrichtigt</li>
                <li>✓ Nehmen Sie den Anruf an oder lehnen Sie ihn ab</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* EMERGENCY VIEW */}
      {currentView === 'emergency' && (
        <EmergencyView senior={senior} household={household} deviceId={deviceId} />
      )}

      {/* STORIES VIEW */}
      {currentView === 'stories' && (
        <StoriesView senior={senior} />
      )}

      {/* MUSIC VIEW */}
      {currentView === 'music' && (
        <MusicView />
      )}

      {/* CONTACTS VIEW */}
      {currentView === 'contacts' && (
        <ContactsView senior={senior} onCallFamily={handleCallFamily} onCallContact={handleCallContact} />
      )}

      {/* SETTINGS VIEW */}
      {currentView === 'settings' && (
        <SettingsView 
          senior={senior}
          onSettingsUpdated={(updatedSenior) => {
            console.log('[DeviceHome] Settings updated:', updatedSenior);
            setSenior(updatedSenior);
          }}
        />
      )}

      {/* WEATHER VIEW */}
      {currentView === 'weather' && (
        <WeatherView senior={senior} />
      )}

      {/* NEWS VIEW */}
      {currentView === 'news' && (
        <NewsView senior={senior} />
      )}

      {/* PRAYER TIMES VIEW */}
      {currentView === 'prayer_times' && (
        <PrayerTimesView senior={senior} onClose={() => setCurrentView('home')} />
      )}

      {/* Voice Control Components (only render if supported) */}
      {voiceControl.isSupported && (
        <>
          <MicButton 
            onClick={voiceControl.startListening}
            isListening={voiceControl.isListening}
            disabled={currentView === 'call'}
          />
          
          <VoiceOverlay 
            isListening={voiceControl.isListening}
            transcript={voiceControl.transcript}
            error={voiceControl.error}
            onCancel={voiceControl.stopListening}
          />
        </>
      )}

      {/* Global Audio Player - Persistent player over Bottom Nav */}
      <GlobalAudioPlayer onOpenMusic={() => setCurrentView('music')} />

      {/* Bottom Navigation */}
      <BottomNav currentView={currentView} onNavigateHome={() => setCurrentView('home')} />

      {/* P2: Screensaver Overlay - ALWAYS rendered, controlled by active prop */}
      <ScreensaverView
        active={settings.screensaver?.enabled && screensaver.isScreensaverActive}
        settings={settings.screensaver}
        photos={screensaverPhotos}
        onExit={() => {
          console.log('[DeviceHome] Exiting screensaver');
          screensaver.resetTimer();
        }}
      />
    </div>
  );
}
